/**
 * Admin – Seasonal Content Scheduling
 *
 * Tammy sees all named seasons (Valentine's Day, Easter, Mother's Day, etc.)
 * with their annual date windows.  She can:
 *   - Create custom seasons (e.g. "Canada Day", "Back to School")
 *   - Edit dates and details on any season
 *   - Enable / disable a season entirely
 *   - See which seasons are LIVE NOW vs. coming up next
 *
 * Phase 1 (now):  View, create, edit, delete seasons.  Tag items (menu items,
 *                 events, promos) to each season.
 * Phase 2 (next): Cron job auto-activates/deactivates tagged items on the right
 *                 dates each year — fully automatic after that.
 */

import { useState, useEffect } from 'react';
import {
  HiPlus, HiPencil, HiTrash, HiCalendar, HiSparkles, HiLightningBolt,
  HiClock, HiCheckCircle,
} from 'react-icons/hi';
import { adminFetch, adminHeaders } from '../lib/adminApi';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Season {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon_emoji: string;
  color: string;
  go_live_month: number;
  go_live_day: number;
  expire_month: number;
  expire_day: number;
  is_active: boolean;
  display_order: number;
  // Hero override fields — when set, the homepage hero auto-swaps during this season
  hero_badge_text:  string | null;
  hero_title:       string | null;
  hero_description: string | null;
  hero_image_url:   string | null;
  // Computed by backend
  item_count: number;
  is_live_now: boolean;
  days_until_live: number;
}

const BLANK_FORM = {
  name:             '',
  description:      '',
  icon_emoji:       '🎉',
  color:            '#8B5CF6',
  go_live_month:    1,
  go_live_day:      1,
  expire_month:     1,
  expire_day:       14,
  is_active:        true,
  // Optional hero override fields
  hero_badge_text:  '',
  hero_title:       '',
  hero_description: '',
  hero_image_url:   '',
};

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const API_URL = import.meta.env.VITE_API_URL || '';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateWindow(s: Season): string {
  const start = `${MONTH_NAMES[s.go_live_month - 1]} ${s.go_live_day}`;
  const end   = `${MONTH_NAMES[s.expire_month - 1]} ${s.expire_day}`;
  return `${start} → ${end}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Seasons() {
  const [seasons, setSeasons]   = useState<Season[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  // Modal state
  const [showModal, setShowModal]   = useState(false);
  const [editing, setEditing]       = useState<Season | null>(null);
  const [form, setForm]             = useState(BLANK_FORM);
  const [saving, setSaving]         = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Season | null>(null);

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  async function fetchSeasons() {
    try {
      const res = await adminFetch(`${API_URL}/api/seasons`);
      if (!res.ok) throw new Error('Failed to load seasons');
      const data = await res.json();
      setSeasons(data.seasons || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchSeasons(); }, []);

  // ─── Modal helpers ──────────────────────────────────────────────────────────

  function openCreate() {
    setEditing(null);
    setForm(BLANK_FORM);
    setShowModal(true);
  }

  function openEdit(s: Season) {
    setEditing(s);
    setForm({
      name:             s.name,
      description:      s.description || '',
      icon_emoji:       s.icon_emoji,
      color:            s.color,
      go_live_month:    s.go_live_month,
      go_live_day:      s.go_live_day,
      expire_month:     s.expire_month,
      expire_day:       s.expire_day,
      is_active:        s.is_active,
      hero_badge_text:  s.hero_badge_text  || '',
      hero_title:       s.hero_title       || '',
      hero_description: s.hero_description || '',
      hero_image_url:   s.hero_image_url   || '',
    });
    setShowModal(true);
  }

  function closeModal() { setShowModal(false); setEditing(null); }

  // ─── Save ────────────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const url    = editing ? `${API_URL}/api/seasons/${editing.id}` : `${API_URL}/api/seasons`;
      const method = editing ? 'PUT' : 'POST';
      const res    = await adminFetch(url, {
        method,
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Save failed');
      }
      await fetchSeasons();
      closeModal();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  // ─── Delete ──────────────────────────────────────────────────────────────────

  async function handleDelete(s: Season) {
    try {
      const res = await adminFetch(`${API_URL}/api/seasons/${s.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      setSeasons(prev => prev.filter(x => x.id !== s.id));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setConfirmDelete(null);
    }
  }

  // ─── Quick toggle active ──────────────────────────────────────────────────

  async function toggleActive(s: Season) {
    try {
      await adminFetch(`${API_URL}/api/seasons/${s.id}`, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ is_active: !s.is_active }),
      });
      setSeasons(prev => prev.map(x => x.id === s.id ? { ...x, is_active: !x.is_active } : x));
    } catch {
      alert('Could not update season');
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">Loading seasons…</div>;
  if (error)   return <div className="text-red-600 p-6">{error}</div>;

  // Split into: live now, upcoming, inactive
  const liveNow  = seasons.filter(s => s.is_live_now);
  const upcoming = seasons.filter(s => s.is_active && !s.is_live_now);
  const inactive = seasons.filter(s => !s.is_active);

  return (
    <div className="space-y-8">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Seasonal Content</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-xl">
            Tag menu items, events, and promotions to a season. When automation is enabled
            (Phase 2), they'll go live and hide themselves automatically every year — no action needed.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm flex-shrink-0"
        >
          <HiPlus className="w-5 h-5" />
          Add Season
        </button>
      </div>

      {/* ── Phase 2 coming-soon banner ── */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
        <HiSparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-amber-800">
            Phase 2 coming soon: Automatic activation 🚀
          </p>
          <p className="text-sm text-amber-700 mt-0.5">
            Right now you can set up your seasons and tag items to them. In the next update, a daily
            scheduler will automatically turn tagged items on and off on the correct dates every year.
            Start tagging items now — they'll be ready to automate.
          </p>
        </div>
      </div>

      {/* ── Live Now ── */}
      {liveNow.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-green-700 mb-3 flex items-center gap-2">
            <HiLightningBolt className="w-4 h-4" /> Live Right Now
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {liveNow.map(s => <SeasonCard key={s.id} season={s} onEdit={openEdit} onDelete={setConfirmDelete} onToggle={toggleActive} />)}
          </div>
        </div>
      )}

      {/* ── Upcoming ── */}
      {upcoming.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-purple-700 mb-3 flex items-center gap-2">
            <HiClock className="w-4 h-4" /> Coming Up
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {upcoming
              .slice()
              .sort((a, b) => a.days_until_live - b.days_until_live)
              .map(s => <SeasonCard key={s.id} season={s} onEdit={openEdit} onDelete={setConfirmDelete} onToggle={toggleActive} />)}
          </div>
        </div>
      )}

      {/* ── Disabled ── */}
      {inactive.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-3 flex items-center gap-2">
            <HiCheckCircle className="w-4 h-4" /> Disabled
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {inactive.map(s => <SeasonCard key={s.id} season={s} onEdit={openEdit} onDelete={setConfirmDelete} onToggle={toggleActive} />)}
          </div>
        </div>
      )}

      {seasons.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <HiCalendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-lg font-medium">No seasons yet</p>
          <p className="text-sm">Click "Add Season" to create your first one.</p>
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">
                {editing ? 'Edit Season' : 'Add New Season'}
              </h2>
            </div>

            <div className="p-6 space-y-5">

              {/* Name + Emoji */}
              <div className="flex gap-3">
                <div className="w-24">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emoji</label>
                  <input
                    type="text"
                    value={form.icon_emoji}
                    onChange={e => setForm(f => ({ ...f, icon_emoji: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-2xl text-center"
                    maxLength={4}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Season Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder='e.g. Mother’s Day'
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              {/* Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Badge Colour</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={form.color}
                    onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                    className="w-10 h-10 rounded border cursor-pointer"
                    aria-label="Season badge colour"
                    title="Season badge colour"
                  />
                  <span className="text-sm text-gray-500">Used for the admin badge colour</span>
                </div>
              </div>

              {/* Date window */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Annual Date Window <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {/* Go live */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Goes live on</p>
                    <div className="flex gap-2">
                      <select
                        value={form.go_live_month}
                        onChange={e => setForm(f => ({ ...f, go_live_month: parseInt(e.target.value) }))}
                        className="flex-1 px-2 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                        aria-label="Go-live month"
                        title="Go-live month"
                      >
                        {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                      </select>
                      <input
                        type="number" min={1} max={31}
                        value={form.go_live_day}
                        onChange={e => setForm(f => ({ ...f, go_live_day: parseInt(e.target.value) }))}
                        className="w-16 px-2 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                        aria-label="Go-live day"
                        title="Go-live day"
                      />
                    </div>
                  </div>
                  {/* Expires */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Expires on</p>
                    <div className="flex gap-2">
                      <select
                        value={form.expire_month}
                        onChange={e => setForm(f => ({ ...f, expire_month: parseInt(e.target.value) }))}
                        className="flex-1 px-2 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                        aria-label="Expire month"
                        title="Expire month"
                      >
                        {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                      </select>
                      <input
                        type="number" min={1} max={31}
                        value={form.expire_day}
                        onChange={e => setForm(f => ({ ...f, expire_day: parseInt(e.target.value) }))}
                        className="w-16 px-2 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                        aria-label="Expire day"
                        title="Expire day"
                      />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Dates repeat every year — set once, works forever.
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  rows={2}
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Reminder notes for yourself about this season"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                  aria-label="Season notes"
                  title="Season notes"
                />
              </div>

              {/* ── Homepage Hero Override (optional) ── */}
              <div className="pt-2 border-t">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Homepage Hero Override <span className="text-gray-400 font-normal normal-case">(optional)</span>
                </p>
                <p className="text-xs text-gray-400 mb-3">
                  When this season is active, the homepage hero will automatically show these values instead of the default.
                  Leave blank to keep the default hero.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Badge Text</label>
                    <input
                      type="text"
                      value={form.hero_badge_text}
                      onChange={e => setForm(f => ({ ...f, hero_badge_text: e.target.value }))}
                      placeholder="e.g. 💕 Valentine's Day Special"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Hero Title</label>
                    <input
                      type="text"
                      value={form.hero_title}
                      onChange={e => setForm(f => ({ ...f, hero_title: e.target.value }))}
                      placeholder="e.g. Sweet Valentine Cakes"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Hero Description</label>
                    <textarea
                      rows={2}
                      value={form.hero_description}
                      onChange={e => setForm(f => ({ ...f, hero_description: e.target.value }))}
                      placeholder="e.g. Celebrate love with a custom cake for your Valentine."
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Hero Image URL</label>
                    <input
                      type="url"
                      value={form.hero_image_url}
                      onChange={e => setForm(f => ({ ...f, hero_image_url: e.target.value }))}
                      placeholder="https://... (leave blank to use default hero image)"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Active toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">Season is active (enable automation when Phase 2 ships)</span>
              </label>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button onClick={closeModal} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim()}
                className="px-5 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create Season')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ── */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete "{confirmDelete.name}"?</h3>
            <p className="text-gray-600 text-sm mb-6">
              This will also remove all items tagged to this season. The items themselves won't be deleted.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Season Card ──────────────────────────────────────────────────────────────

function SeasonCard({ season: s, onEdit, onDelete, onToggle }: {
  season: Season;
  onEdit: (s: Season) => void;
  onDelete: (s: Season) => void;
  onToggle: (s: Season) => void;
}) {
  return (
    <div
      className={`bg-white border-2 rounded-xl p-5 shadow-sm transition ${
        s.is_live_now ? 'border-green-400' : s.is_active ? 'border-gray-200' : 'border-gray-100 opacity-60'
      }`}
    >
      {/* Top row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{s.icon_emoji}</span>
          <div>
            <h3 className="font-bold text-gray-900 text-sm leading-tight">{s.name}</h3>
            {s.is_live_now && (
              <span className="inline-block text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full mt-0.5">
                🟢 Live Now
              </span>
            )}
            {!s.is_live_now && s.is_active && (
              <span className="inline-block text-xs text-gray-500 mt-0.5">
                In {s.days_until_live} day{s.days_until_live !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        {/* colour dot */}
        <div className="w-4 h-4 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: s.color }} />
      </div>

      {/* Date window */}
      <p className="text-xs text-gray-500 flex items-center gap-1 mb-3">
        <HiCalendar className="w-3.5 h-3.5" />
        {formatDateWindow(s)} · repeats yearly
      </p>

      {/* Item count */}
      <p className="text-xs text-gray-400 mb-4">
        {s.item_count === 0
          ? 'No items tagged yet'
          : `${s.item_count} item${s.item_count !== 1 ? 's' : ''} tagged`}
      </p>

      {/* Actions */}
      <div className="flex items-center justify-between">
        {/* Enable / Disable toggle */}
        <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-600">
          <input
            type="checkbox"
            checked={s.is_active}
            onChange={() => onToggle(s)}
            className="w-3.5 h-3.5 text-purple-600"
          />
          {s.is_active ? 'Active' : 'Disabled'}
        </label>

        <div className="flex gap-1">
          <button
            onClick={() => onEdit(s)}
            title="Edit season"
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
          >
            <HiPencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(s)}
            title="Delete season"
            className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600"
          >
            <HiTrash className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
